import { DefaultLoader, LoaderContainer } from 'components/Loader';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  setOrder,
  setPage,
  setSearch,
  setTags,
  setTake,
} from 'store/actions/admin/mentorLists';
import { StoreState } from 'store/reducers';
import useSWR, { SWRResponse } from 'swr';
import GetOrderValue from 'types/GetOrderValue';
import Tags from 'types/Tags';
import { Action } from 'types/typeguards/reducerActions';
import User from 'types/User';
import { getIsMenteeStatus } from 'utils/menteeTags';
import getTagsQueryParams from 'utils/tagQueryParam';
import { MentorEditContext } from '..';
import AssociateFilter from './AssociateFilter';
import AssociateList from './AssociateList';

export const tagColumnNames = [
  'status',
  'jobSearch',
  'careerPlan',
  'studies',
  'entrepreneurship',
  'expertiseFields',
  'completedFormation',
  'validatedDiploma',
  'formationMatchingProject',
  'formationState',
  'intendedFormation',
  'intendedDiploma',
  'consideration',
  'hobby',
  'volunteerState',
] as const;
export type TagColumnNames = typeof tagColumnNames[number];

export const sortableColumns = [
  'matching',
  'firstName',
  'lastName',
  'createdAt',
] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;

type RetrievedData = {
  mentees: User[] | undefined;
  count: number | undefined;
  pages: number | undefined;
};

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  tags: Tags<TagColumnNames>;
  setTags: (tag: Tags<TagColumnNames>) => void;
  take: number;
  setTake: (take: number) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};
type ContextType = {
  mentor: User | undefined;
  mentees: User[] | undefined;
  pages: number | undefined;
  loading: boolean;
} & Props;
export const AssociateContext = React.createContext({} as ContextType);
const { Provider } = AssociateContext;

const useCustomSWR = (
  id: number | undefined,
  take: number,
  page: number,
  search: string,
  tags: Tags<TagColumnNames>,
  order: OrderValue,
): SWRResponse<RetrievedData> => {
  const { data, error, mutate, isValidating } = useSWR<RetrievedData>(
    `/mentorship/mentor/${id}/associate?take=${take}&page=${page}\
		${search ? `&search=${search}` : ''}\
		${
      tags.status[0]
        ? tags.status
            .map((tag) =>
              getIsMenteeStatus(tag)
                ? `&menteeTags=${tag}`
                : `&mentorshipTags=${tag}`,
            )
            .join('')
        : ''
    }\
		${getTagsQueryParams<TagColumnNames>(tags, [
      'jobSearch',
      'careerPlan',
      'studies',
      'entrepreneurship',
      'expertiseFields',
      'completedFormation',
      'validatedDiploma',
      'formationMatchingProject',
      'formationState',
      'intendedFormation',
      'intendedDiploma',
      'consideration',
      'hobby',
      'volunteerState',
    ])}\
&order=${order}
		`,
  );
  if (!id) {
    return { data: undefined, error, mutate, isValidating };
  }
  return { data, error, mutate, isValidating };
};
const Associate: FC<Props> = ({
  children,
  page,
  setPage,
  search,
  setSearch,
  tags,
  setTags,
  take,
  setTake,
  order,
  setOrder,
}) => {
  const { mentor } = useContext(MentorEditContext);

  const { data, error } = useCustomSWR(
    mentor?.id,
    take,
    page,
    search,
    tags,
    order,
  );

  const [retrievedData, setRetrievedData] = useState<RetrievedData>({
    mentees: undefined,
    count: undefined,
    pages: undefined,
  });
  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  // Reset page to 0 if the stored page is out of range
  useEffect(() => {
    if (data?.pages && data?.pages <= page) {
      setPage(0);
    }
  }, [data, page, setPage]);

  const value = useMemo(
    () => ({
      mentor,
      mentees: retrievedData.mentees,
      pages: retrievedData.pages,
      page,
      setPage,
      search,
      setSearch,
      tags,
      setTags,
      loading: !data,
      take,
      setTake,
      order,
      setOrder,
    }),
    [
      mentor,
      search,
      setSearch,
      tags,
      setTags,
      page,
      setPage,
      data,
      retrievedData,
      take,
      setTake,
      order,
      setOrder,
    ],
  );

  return error ? (
    error.response?.status === 403 ? (
      <p>Ce mentor a déjà assez de mentorés.</p>
    ) : error.response?.status === 400 ? (
      <p>L'utilisateur n'a pas rempli son formulaire correctement.</p>
    ) : (
      <p>Une erreur est survenue.</p>
    )
  ) : retrievedData.mentees ? (
    <Provider value={value}>
      <section>{children}</section>
    </Provider>
  ) : (
    <LoaderContainer height="auto">
      <DefaultLoader />
    </LoaderContainer>
  );
};

const Usage: FC<Props> = (props) => {
  return (
    <Associate {...props}>
      <AssociateFilter />
      <AssociateList />
    </Associate>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.admin.mentorLists.associate.page,
    search: state.admin.mentorLists.associate.search,
    tags: state.admin.mentorLists.associate.tags,
    take: state.admin.mentorLists.associate.take,
    order: state.admin.mentorLists.associate.order,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('associate', page)),
    setSearch: (search: string) => dispatch(setSearch('associate', search)),
    setTags: (tags: Tags<TagColumnNames>) =>
      dispatch(setTags('associate', tags)),
    setTake: (take: number) => dispatch(setTake('associate', take)),
    setOrder: (order: OrderValue) => dispatch(setOrder('associate', order)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Usage);
